import { useCallback, useEffect, useState } from 'react'
import { ChevronRight, Loader2, MapPin } from 'lucide-react'
import { ErrorStrip } from '@/shared/ui/states'
import { explain } from '@/shared/api/problem'
import { fmtNumber } from '@/shared/lib/format'
import { cn } from '@/lib/utils'
import { unitTree } from './api'
import { UNIT_TYPE_LABEL, type UnitTreeNode } from '@/shared/api/types'

/**
 * Lazy hierarchy browser.
 *
 * The tree is fetched one level at a time. 46,000 polling stations cannot arrive
 * in a single payload, and the endpoint's `depth` parameter exists precisely so
 * the client does not ask for them — a branch loads its children the first time
 * it is opened, and is cached from then on.
 *
 * The whole component is deliberately not virtualised: a single open branch is
 * at most ~1,450 wards under a constituency's parent, and the container scrolls.
 * Virtualisation earns its keep on a flat 46k list, which is what the table view
 * is for.
 */
export function UnitTree({
  selectedId, onSelect,
}: {
  selectedId?: string
  onSelect: (id: string) => void
}) {
  const [roots, setRoots] = useState<UnitTreeNode[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setRoots(await unitTree(undefined, 1))
    } catch (err) {
      setError(explain(err))
      setRoots(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  if (loading && !roots) {
    return (
      <div className="px-4 py-8 text-center text-erp-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading hierarchy…
      </div>
    )
  }

  return (
    <div>
      <ErrorStrip message={error} />
      {(roots ?? []).length === 0 && !error ? (
        <p className="px-4 py-6 text-erp-sm text-muted-foreground">
          No units yet.
        </p>
      ) : (
        <ul className="py-1">
          {(roots ?? []).map((n) => (
            <TreeRow key={n.id} node={n} depth={0} selectedId={selectedId} onSelect={onSelect} />
          ))}
        </ul>
      )}
    </div>
  )
}

function TreeRow({
  node, depth, selectedId, onSelect,
}: {
  node: UnitTreeNode
  depth: number
  selectedId?: string
  onSelect: (id: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [children, setChildren] = useState<UnitTreeNode[] | null>(node.children ?? null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function toggle() {
    if (!node.hasChildren) return
    if (open) { setOpen(false); return }
    setOpen(true)
    if (children) return          // already fetched — do not re-request on every open
    setLoading(true)
    setError(null)
    try {
      const level = await unitTree(node.id, 1)
      // The endpoint echoes the requested root, so unwrap to its children.
      setChildren(level[0]?.children ?? level.filter((n) => n.id !== node.id))
    } catch (err) {
      setError(explain(err))
      setOpen(false)
    } finally {
      setLoading(false)
    }
  }

  const selected = node.id === selectedId
  const dimmed = node.status !== 'ACTIVE'

  return (
    <li>
      <div
        className={cn(
          'flex items-center gap-1 pr-2 hover:bg-muted/40 min-h-[28px]',
          selected && 'bg-chocolate-soft/40 border-l-[3px] border-l-chocolate',
        )}
        style={{ paddingLeft: `${8 + depth * 14 - (selected ? 3 : 0)}px` }}
      >
        <button
          type="button"
          onClick={toggle}
          aria-label={node.hasChildren ? (open ? 'Collapse' : 'Expand') : undefined}
          disabled={!node.hasChildren}
          className={cn(
            'h-6 w-6 shrink-0 inline-flex items-center justify-center rounded',
            node.hasChildren ? 'hover:bg-muted text-muted-foreground' : 'opacity-0 cursor-default',
          )}
        >
          {loading
            ? <Loader2 className="h-3 w-3 animate-spin" />
            : <ChevronRight className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-90')} />}
        </button>

        <button
          type="button"
          onClick={() => onSelect(node.id)}
          className="flex-1 min-w-0 text-left py-1 flex items-center gap-2"
          title={`${UNIT_TYPE_LABEL[node.unitType] ?? node.unitType} · ${node.code}`}
        >
          {node.unitType === 'POLLING_STATION' && (
            <MapPin className="h-3 w-3 shrink-0 text-muted-foreground" />
          )}
          <span className={cn('text-erp-sm truncate', dimmed && 'text-muted-foreground line-through')}>
            {node.name}
          </span>
          <span className="text-erp-xs font-mono text-muted-foreground shrink-0">{node.code}</span>
          {node.stationCount > 0 && (
            <span className="ml-auto text-erp-xs text-muted-foreground erp-metric shrink-0">
              {fmtNumber(node.stationCount)}
            </span>
          )}
        </button>
      </div>

      {error && (
        <div className="text-erp-xs text-red-700 px-3 py-1" style={{ paddingLeft: `${28 + depth * 14}px` }}>
          {error}
        </div>
      )}

      {open && children && (
        <ul>
          {children.map((c) => (
            <TreeRow key={c.id} node={c} depth={depth + 1} selectedId={selectedId} onSelect={onSelect} />
          ))}
          {children.length === 0 && (
            <li
              className="text-erp-xs text-muted-foreground py-1"
              style={{ paddingLeft: `${28 + (depth + 1) * 14}px` }}
            >
              No children
            </li>
          )}
        </ul>
      )}
    </li>
  )
}
